import { Conversation } from '@elevenlabs/client';
import { CallTranscriptPart, Company, Project } from '../types';

type TranscriptRole = 'model' | 'user';

export class ElevenLabsRealtimeService {
  private conversation: any | null = null;
  private isConnected = false;
  private transcript: Array<{ role: TranscriptRole; text: string; timestamp: number }> = [];
  private lastError: string | null = null;
  private audioLevels = { input: 0, output: 0 };
  private userMuted = false;

  private pushTranscript(role: TranscriptRole, text: string) {
    const cleaned = text.trim();
    if (!cleaned) return;

    const now = Date.now();
    const previous = this.transcript[this.transcript.length - 1];
    if (previous && previous.role === role && now - previous.timestamp < 1500) {
      previous.text = `${previous.text} ${cleaned}`.trim();
      previous.timestamp = now;
      return;
    }

    this.transcript.push({ role, text: cleaned, timestamp: now });
  }

  getTranscript() {
    return this.transcript
      .map((entry) => `${entry.role === 'model' ? 'BDAT' : 'Contactpersoon'}: ${entry.text}`)
      .join('\n');
  }

  getTranscriptParts(): CallTranscriptPart[] {
    return this.transcript.map((entry) => ({
      speaker: entry.role === 'model' ? 'agent' : 'contact',
      text: entry.text,
    }));
  }

  setMuted(muted: boolean) {
    this.userMuted = muted;
    if (this.conversation) {
      // @elevenlabs/client supports setInputMute
      try {
        this.conversation.setInputMute(muted);
      } catch (error) {
        console.warn('[ElevenLabs Realtime] Mute toggle failed:', error);
      }
    }
  }

  getAudioLevels() {
    return this.audioLevels;
  }

  getLastError() {
    return this.lastError;
  }

  async connect(
    project: Project | null,
    company: Company | null,
    onStatusChange: (status: string) => void,
    options: {
      agentId: string;
    }
  ) {
    this.disconnect();
    onStatusChange('connecting');

    if (!options.agentId?.trim()) {
      const errorMsg = 'ElevenLabs Agent ID ontbreekt. Configureer een geldig Agent ID in de steminstellingen.';
      this.lastError = errorMsg;
      onStatusChange('error');
      throw new Error(errorMsg);
    }

    this.transcript = [];
    this.lastError = null;
    this.audioLevels = { input: 0, output: 0 };

    try {
      console.log(`[ElevenLabs Realtime] Starting WebRTC session for Agent: ${options.agentId}`);

      // Geef de agent mee namens wie hij belt, zodat hij zich correct voorstelt.
      const dynamicVariables: Record<string, string> = {
        client_name: project?.name || 'de opdrachtgever',
        call_goal: company?.name
          ? `een afspraak inplannen met ${company.name}`
          : 'een korte afspraak inplannen',
      };
      
      const conversation = await Conversation.startSession({
        agentId: options.agentId.trim(),
        dynamicVariables,
        onConnect: () => {
          this.isConnected = true;
          onStatusChange('active');
          console.log('[ElevenLabs Realtime] WebRTC Session connected');
        },
        onDisconnect: () => {
          this.isConnected = false;
          onStatusChange('disconnected');
          console.log('[ElevenLabs Realtime] WebRTC Session disconnected');
        },
        onMessage: (message: any) => {
          // message has format: { message: string, source: 'user' | 'ai' }
          if (message?.message && message?.source) {
            const role: TranscriptRole = message.source === 'ai' ? 'model' : 'user';
            this.pushTranscript(role, message.message);
          }
        },
        onError: (error: any) => {
          console.error('[ElevenLabs Realtime] Error:', error);
          this.lastError = error?.message || JSON.stringify(error);
          onStatusChange('error');
        },
      });

      this.conversation = conversation;
    } catch (error: any) {
      console.error('[ElevenLabs Realtime] Connection failed:', error);
      const errorMsg = error?.message || 'Kan geen verbinding maken met ElevenLabs Realtime.';
      this.lastError = errorMsg;
      onStatusChange('error');
      throw new Error(errorMsg);
    }
  }

  disconnect() {
    if (this.conversation) {
      try {
        this.conversation.endSession();
      } catch (error) {
        console.warn('[ElevenLabs Realtime] End session failed:', error);
      }
      this.conversation = null;
    }
    this.isConnected = false;
    this.lastError = null;
    this.audioLevels = { input: 0, output: 0 };
  }
}
